/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { readString } from 'react-papaparse';
import { normalizeFilterSortValue } from '../modules/webgroup-filters/components/filter-sort-normalizer';

export type FiltersSortOrder = {
    'Size/Length & Diameter': SortOrder[];
    'Diam/Thread Size (imperial)': SortOrder[];
    'Diam/Thread Size (metric)': SortOrder[];
};

export type SortOrder = {
    Value: string;
    SortOrder: string;
};

export class GetFiltersSortingDataInput implements Msdyn365.IActionInput {
    constructor() {}

    public getCacheKey = () => `GetFiltersSortingDataInput`;
    public getCacheObjectType = () => 'GetFiltersSortingDataInput';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetFiltersSortingData {
    FiltersSortOrder: FiltersSortOrder[];
}

const createInput = (ctx: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetFiltersSortingDataInput();
};

async function action(input: GetFiltersSortingDataInput, ctx: Msdyn365.IActionContext): Promise<IGetFiltersSortingData> {
    const { url } = ctx.requestContext.app.config.filtersSorDataSource;
    const { data } = await Msdyn365.sendRequest<string>(url, 'get');
    const base = await parseDataSource(data.trim());
    return { FiltersSortOrder: groupDataByFilter(base) as any };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetFiltersSortingData>>action,
    id: 'GetFiltersSortingData',
    input: createInput
});

async function parseDataSource(csv: string): Promise<IGetFiltersSortingData[]> {
    return new Promise(resolve => {
        readString(csv, { complete: (records: any) => resolve(records.data), header: true });
    });
}

function groupDataByFilter(base: any[]) {
    const columnNames = [
        'Size/Length & Diameter',
        'Diam/Thread Size (imperial)',
        'Diam/Thread Size (metric)',
        'Sort Order',
        'Sort Order_1',
        'Sort Order_2'
    ];

    const entries = {
        [columnNames[0]]: [] as SortOrder[],
        [columnNames[1]]: [] as SortOrder[],
        [columnNames[2]]: [] as SortOrder[]
    };

    base.forEach(obj => {
        obj[columnNames[3]] &&
            entries[columnNames[0]].push({
                Value: normalizeFilterSortValue(obj[columnNames[0]], false),
                SortOrder: obj[columnNames[3]]
            });

        obj[columnNames[4]] &&
            entries[columnNames[1]].push({
                Value: normalizeFilterSortValue(obj[columnNames[1]], true),
                SortOrder: obj[columnNames[4]]
            });

        obj[columnNames[5]] &&
            entries[columnNames[2]].push({
                Value: normalizeFilterSortValue(obj[columnNames[2]], false),
                SortOrder: obj[columnNames[5]]
            });
    });

    return entries;
}
